/**
 * Utility to enable verbose logging in the browser console
 * Usage:
 * 1. In browser console: window.enableVerboseLogging()
 * 2. Or import and call: import { enableVerboseLogging } from './utils/enableLogging';
 */

export function enableVerboseLogging() {
  // Store original console methods
  const originalLog = console.log;
  const originalError = console.error;
  const originalWarn = console.warn;
  const originalInfo = console.info;

  // Override localStorage to force debug logs
  localStorage.setItem('ENABLE_DEBUG_LOGS', 'true');

  // Add timestamp and styling to console methods
  console.log = function(...args) {
    const timestamp = new Date().toISOString();
    originalLog(`%c[${timestamp}] LOG:`, 'color: #4CAF50; font-weight: bold;', ...args);
  };

  console.error = function(...args) {
    const timestamp = new Date().toISOString();
    originalError(`%c[${timestamp}] ERROR:`, 'color: #f44336; font-weight: bold;', ...args);
  };

  console.warn = function(...args) {
    const timestamp = new Date().toISOString();
    originalWarn(`%c[${timestamp}] WARN:`, 'color: #FF9800; font-weight: bold;', ...args);
  };

  console.info = function(...args) {
    const timestamp = new Date().toISOString();
    originalInfo(`%c[${timestamp}] INFO:`, 'color: #2196F3; font-weight: bold;', ...args);
  };

  // Log all fetch requests
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const [url, options = {}] = args;
    console.log('%c🌐 API Request:', 'color: #9C27B0; font-weight: bold;', {
      url,
      method: options.method || 'GET',
      headers: options.headers,
      body: options.body
    });

    return originalFetch.apply(this, args)
      .then(response => {
        const clonedResponse = response.clone();
        console.log('%c✅ API Response:', 'color: #4CAF50; font-weight: bold;', {
          url,
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries())
        });

        // Try to log response body
        clonedResponse.json()
          .then(data => {
            console.log('%c📦 Response Data:', 'color: #00BCD4; font-weight: bold;', data);
          })
          .catch(() => {
            // Not JSON, try text
            clonedResponse.text()
              .then(text => {
                console.log('%c📄 Response Text:', 'color: #00BCD4; font-weight: bold;', text);
              })
              .catch(() => {});
          });

        return response;
      })
      .catch(error => {
        console.error('%c❌ API Error:', 'color: #f44336; font-weight: bold;', {
          url,
          error: error.message
        });
        throw error;
      });
  };

  // Log all localStorage operations
  const originalSetItem = Storage.prototype.setItem;
  const originalGetItem = Storage.prototype.getItem;
  const originalRemoveItem = Storage.prototype.removeItem;

  Storage.prototype.setItem = function(key, value) {
    console.log('%c💾 LocalStorage SET:', 'color: #FF5722; font-weight: bold;', { key, value });
    return originalSetItem.apply(this, arguments);
  };

  Storage.prototype.getItem = function(key) {
    const value = originalGetItem.apply(this, arguments);
    console.log('%c💾 LocalStorage GET:', 'color: #795548; font-weight: bold;', { key, value });
    return value;
  };

  Storage.prototype.removeItem = function(key) {
    console.log('%c💾 LocalStorage REMOVE:', 'color: #607D8B; font-weight: bold;', { key });
    return originalRemoveItem.apply(this, arguments);
  };

  // Log React renders (if React DevTools is available)
  if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
    const originalOnCommitFiberRoot = hook.onCommitFiberRoot;

    hook.onCommitFiberRoot = function(id, root, priorityLevel) {
      console.log('%c⚛️ React Render:', 'color: #61DAFB; font-weight: bold;', {
        componentCount: root?.current?.child ? countComponents(root.current) : 0,
        priorityLevel
      });

      if (originalOnCommitFiberRoot) {
        return originalOnCommitFiberRoot.apply(this, arguments);
      }
    };
  }

  console.log('%c✨ Verbose logging enabled!', 'color: #4CAF50; font-size: 16px; font-weight: bold;');
  console.log('Features enabled:');
  console.log('  ✅ Timestamped console logs');
  console.log('  ✅ API request/response logging');
  console.log('  ✅ LocalStorage operations');
  console.log('  ✅ React render tracking');

  return {
    disable: () => {
      console.log = originalLog;
      console.error = originalError;
      console.warn = originalWarn;
      console.info = originalInfo;
      window.fetch = originalFetch;
      Storage.prototype.setItem = originalSetItem;
      Storage.prototype.getItem = originalGetItem;
      Storage.prototype.removeItem = originalRemoveItem;
      localStorage.removeItem('ENABLE_DEBUG_LOGS');
      console.log('Verbose logging disabled');
    }
  };
}

// Helper function to count React components
function countComponents(fiber, count = 0) {
  if (!fiber) return count;

  if (fiber.tag === 0 || fiber.tag === 1 || fiber.tag === 2) {
    count++;
  }

  if (fiber.child) {
    count = countComponents(fiber.child, count);
  }

  if (fiber.sibling) {
    count = countComponents(fiber.sibling, count);
  }

  return count;
}

// Auto-enable if localStorage flag is set
if (typeof window !== 'undefined') {
  if (localStorage.getItem('ENABLE_DEBUG_LOGS') === 'true') {
    enableVerboseLogging();
  }

  // Expose to window for easy console access
  window.enableVerboseLogging = enableVerboseLogging;
  window.enableLogs = enableVerboseLogging; // Shorter alias
}

export default enableVerboseLogging;